'use client';

import {
	Alert,
	Anchor,
	Box,
	Button,
	Card,
	Checkbox,
	Flex,
	PasswordInput,
	Text,
	TextInput,
	Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { SlArrowRight } from 'react-icons/sl';
import { AppPaths } from '@/configs/appPaths';
import { FC, useEffect, useState } from 'react';
import { signIn } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { validateEmail } from '@/utilities';
import AuthApi from '@/remoteApi/AuthApi';
import { jwtDecode } from 'jwt-decode';

interface Props {
	emailConfirmed: boolean;
	activationToken?: string;
}

interface LoginFormValues {
	email: string;
	password: string;
	rememberMe: boolean;
}

export const ClientLogin: FC<Props> = ({ emailConfirmed, activationToken }) => {
	const router = useRouter();
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [activationSuccess, setActivationSuccess] = useState(false);

	useEffect(() => {
		if (activationToken) {
			const activateAccount = async () => {
				try {
					await AuthApi.verifyEmail(activationToken);
					setActivationSuccess(true);
					setError(null);

					try {
						const decodedToken = jwtDecode(activationToken) as {
							email?: string;
						};
						if (decodedToken?.email) {
							form.setFieldValue('email', decodedToken.email);
						}
					} catch {}
				} catch (error) {
					console.error('Błąd podczas aktywacji konta:', error);
					setError(
						'Nie udało się aktywować konta. Nieprawidłowy lub wygasły token.'
					);
				}
			};
			activateAccount();
		}
	}, [activationToken]);

	const form = useForm<LoginFormValues>({
		initialValues: {
			email: '',
			password: '',
			rememberMe: false,
		},
		validate: {
			email: (value) =>
				validateEmail(value) ? null : 'Nieprawidłowy adres email',
			password: (value) => (value.length > 0 ? null : 'Hasło jest wymagane'),
		},
	});

	const handleLogin = async (values: LoginFormValues) => {
		try {
			setIsLoading(true);
			setError(null);

			const res = await signIn('credentials', {
				email: values.email,
				password: values.password,
				redirect: false,
			});

			if (res?.error) {
				setError(
					res.error === 'CredentialsSignin'
						? 'Nieprawidłowy email lub hasło'
						: 'Wystąpił błąd podczas logowania'
				);
				return;
			}

			if (res?.ok) {
				router.push(AppPaths.ACCOUNT || '/');
				router.refresh();
			}
		} catch (err) {
			setError('Wystąpił nieoczekiwany błąd. Spróbuj ponownie później.');
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Box>
			{emailConfirmed && (
				<Alert color="green" mb="md" title="Email potwierdzony">
					Teraz możesz się zalogować
				</Alert>
			)}

			{error && (
				<Alert color="red" mb="md" title="Błąd logowania">
					{error}
				</Alert>
			)}

			{activationSuccess && (
				<Alert color="green" mb="md" title="Aktywacja zakończona sukcesem">
					Twoje konto zostało pomyślnie aktywowane. Możesz się teraz zalogować.
				</Alert>
			)}

			<Title order={2} c="black" mb="sm">
				Zaloguj się
			</Title>

			<Card component="form" onSubmit={form.onSubmit(handleLogin)}>
				<TextInput
					required
					label="Email"
					placeholder="twoj@email.com"
					mb="md"
					{...form.getInputProps('email')}
				/>

				<PasswordInput
					required
					label="Hasło"
					placeholder="Twoje hasło"
					mb="md"
					{...form.getInputProps('password')}
				/>

				<Checkbox
					mb="md"
					label="Zapamiętaj mnie"
					{...form.getInputProps('rememberMe', { type: 'checkbox' })}
				/>

				<Button
					fullWidth
					mb="xl"
					type="submit"
					loading={isLoading}
					disabled={isLoading}
				>
					<Flex gap="sm" align="center">
						<Text fw="bold">Zaloguj się</Text>
						<SlArrowRight size={20} />
					</Flex>
				</Button>

				<Text mb="sm">
					Jeśli nie pamiętasz hasła,{' '}
					<Anchor href={`/${AppPaths.PASSWORD_RESET}`} underline="always">
						kliknij tutaj
					</Anchor>
				</Text>

				<Text mb="sm">
					Jeśli nie posiadasz konta,{' '}
					<Anchor href={`/${AppPaths.REGISTER}`} underline="always">
						kliknij tutaj
					</Anchor>
				</Text>
			</Card>
		</Box>
	);
};
